import type { LocationType } from '~/client/graph'

type MapViewHierarchyItem = {
  readonly dataObject: {
    readonly id: string
    readonly type: LocationType
  } | null
  readonly parent: {
    readonly parentId?: string
  } | null
}

/** An ordered, indexed snapshot of map-view parent relationships. */
export interface IMapViewItemHierarchy<T> {
  readonly rootItems: readonly T[]
  readonly childrenOf: (parent: T) => readonly T[]
}

/**
 * Indexes map-view parent relationships by parent id.
 * By id alone: an id names one location whatever its type. The client no
 * longer selects parentType, which could be stale - "Location", or the parent's
 * kind before it changed - and a child keyed by it would vanish from under its
 * parent.
 * Construction is linear in the item count; child lookup does not rescan the
 * input. Roots and siblings retain input order. Items with any parent value
 * are not promoted to roots when that parent is absent from the input.
 */
export function createMapViewItemHierarchy<T extends MapViewHierarchyItem>(
  items: readonly T[],
): IMapViewItemHierarchy<T> {
  const rootItems: T[] = []
  const childrenByParentId = new Map<string | undefined, T[]>()

  for (const item of items) {
    const parent = item.parent
    if (!parent) {
      rootItems.push(item)
      continue
    }

    const children = childrenByParentId.get(parent.parentId)
    if (children) {
      children.push(item)
    } else {
      childrenByParentId.set(parent.parentId, [item])
    }
  }

  return {
    rootItems,
    childrenOf: parent => {
      const dataObject = parent.dataObject
      if (!dataObject) {
        return []
      }

      return childrenByParentId.get(dataObject.id) ?? []
    },
  }
}
